import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Search, Upload, Pencil, NotebookPen, CalendarClock, MessageCircle, Trash2, ArrowUpDown, Check, Clock } from "lucide-react";
import { Tag, EmptyState, PrimaryBtn, GhostBtn, Modal } from "./CrmUI";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../ui/hover-card";
import { ClientModal, NotesModal, FollowupModal } from "./CrmModals";
import { usePersistentState } from "./usePersistentState";
import { getClients, getCrmConfig, deleteClient, importClients, waLink, fmtBday, workTagClasses } from "./crmApi";

// Convert stored note HTML to plain text for compact previews (read-only, safe).
const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
};

const IconBtn = ({ onClick, title, children, testId, danger }) => (
    <button onClick={onClick} title={title} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${danger ? "text-oth-ink3 hover:text-red-600 hover:bg-red-50" : "text-oth-ink2 hover:text-oth-ink hover:bg-[#EFEFEA]"}`} data-testid={testId}>{children}</button>
);

const SortHead = ({ label, k, span, sort, onSort, testId }) => (
    <button onClick={() => onSort(k)} className={`${span} flex items-center gap-1 text-left hover:text-oth-ink transition-colors`} data-testid={testId}>
        {label} <ArrowUpDown size={11} className={sort.key === k ? "opacity-90" : "opacity-30"} />
    </button>
);

const parseCsv = (text) => {    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    const split = (line) => {
        const out = []; let cur = ""; let q = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
            else if (ch === "," && !q) { out.push(cur); cur = ""; }
            else cur += ch;
        }
        out.push(cur); return out;
    };
    const headers = split(lines[0]).map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
    return lines.slice(1).map((line) => {
        const cells = split(line);
        const row = {};
        headers.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
        return row;
    });
};

const CsvImportModal = ({ open, onClose, onDone }) => {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => { setRows(parseCsv(String(reader.result))); };
        reader.readAsText(file);
    };

    const doImport = async () => {
        if (!rows.length) { toast.error("Nothing to import."); return; }
        setBusy(true);
        try {
            const r = await importClients(rows);
            const parts = [`${r.imported} added`];
            if (r.updated) parts.push(`${r.updated} updated`);
            if (r.skipped) parts.push(`${r.skipped} unchanged`);
            toast.success(`Import complete — ${parts.join(", ")}.`);
            onDone(); onClose(); setRows([]);
        }
        catch (e) { toast.error(e?.response?.data?.detail || "Import failed."); }
        finally { setBusy(false); }
    };

    return (
        <Modal open={open} onClose={onClose} title="Import clients (CSV)" subtitle="Columns: name, email, phone, company, isb_batch, categories, birthday, will_created" maxWidth="max-w-xl" testId="csv-modal">
            <div className="rounded-2xl border border-dashed border-oth-line bg-white p-6 text-center">
                <input type="file" accept=".csv" onChange={onFile} className="block w-full text-sm font-body text-oth-ink2 file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#0A0A0A] file:text-white file:text-sm file:cursor-pointer" data-testid="csv-file" />
                <div className="text-[11px] text-oth-ink3 mt-3 font-body">First row must be headers. <span className="text-oth-ink">name</span> is required; <span className="text-oth-ink">categories</span> can be semicolon-separated (e.g. MF;SIP). Existing clients (same <span className="text-oth-ink">Name + Email + Mobile</span>) are matched — any new or changed fields are merged into them instead of creating duplicates.</div>
            </div>
            {rows.length > 0 && (
                <div className="mt-4 text-sm font-body text-oth-ink2" data-testid="csv-preview">
                    <span className="font-medium text-oth-ink">{rows.length}</span> rows detected. First: <span className="text-oth-ink">{rows[0].name || "(no name)"}</span>
                </div>
            )}
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="csv-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={doImport} disabled={busy || !rows.length} testId="csv-import">{busy ? "Importing…" : "Import"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

export const ClientsCRM = () => {
    const [clients, setClients] = useState(null);
    const [config, setConfig] = useState(null);
    const [query, setQuery] = useState("");
    const [catFilter, setCatFilter] = useState("");
    const [tagFilter, setTagFilter] = useState("");
    const [sort, setSort] = usePersistentState("oth-clients-sort", { key: "created_at", dir: "desc" });
    const [editClient, setEditClient] = useState(undefined);
    const [notesClient, setNotesClient] = useState(null);
    const [followClient, setFollowClient] = useState(null);
    const [csvOpen, setCsvOpen] = useState(false);

    const reload = async () => {
        const [c, cfg] = await Promise.all([getClients(), getCrmConfig()]);
        setClients(c); setConfig(cfg);
    };
    useEffect(() => {
        (async () => {
            try {
                const [c, cfg] = await Promise.all([getClients(), getCrmConfig()]);
                setClients(c); setConfig(cfg);
            } catch { toast.error("Could not load clients."); }
        })();
    }, []);

    const onSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
    const familyName = (id) => clients?.find((c) => c.id === id)?.name;
    const reloadConfig = async () => { try { setConfig(await getCrmConfig()); } catch { /* ignore */ } };
    const tagColor = (label) => (config?.work_tags || []).find((t) => t.label === label)?.color;

    const rows = useMemo(() => {
        if (!clients) return [];
        let r = clients;
        if (catFilter) r = r.filter((c) => (c.categories || []).includes(catFilter));
        if (tagFilter) r = r.filter((c) => (c.work_tags || []).includes(tagFilter));
        const q = query.trim().toLowerCase();
        if (q) r = r.filter((c) => [c.name, c.email, c.phone, c.company, c.isb_batch].join(" ").toLowerCase().includes(q));
        return [...r].sort((a, b) => {
            if (sort.key === "pending_work") {
                const na = a.pending_work || 0, nb = b.pending_work || 0;
                return sort.dir === "asc" ? na - nb : nb - na;
            }
            const va = (a[sort.key] ?? "").toString().toLowerCase();
            const vb = (b[sort.key] ?? "").toString().toLowerCase();
            return va < vb ? (sort.dir === "asc" ? -1 : 1) : va > vb ? (sort.dir === "asc" ? 1 : -1) : 0;
        });
    }, [clients, query, catFilter, tagFilter, sort]);

    const doDelete = async (c) => {
        if (!window.confirm(`Delete client "${c.name}"?`)) return;
        try { await deleteClient(c.id); toast.success("Client deleted."); reload(); }
        catch { toast.error("Could not delete."); }
    };

    if (!clients || !config) return <div className="font-editorial italic text-oth-ink3" data-testid="clients-crm-loading">Loading clients…</div>;

    return (
        <div data-testid="clients-crm">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-oth-ink3" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search clients…" className="w-full pl-9 pr-3 py-2.5 bg-white border border-oth-line/70 rounded-xl font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="clients-search" />
                </div>
                <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="bg-white border border-oth-line/70 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="clients-cat-filter">
                    <option value="">All categories</option>
                    {(config.client_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="bg-white border border-oth-line/70 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="clients-tag-filter">
                    <option value="">All work tags</option>
                    {(config.work_tags || []).map((t) => <option key={t.label} value={t.label}>{t.label}</option>)}
                </select>
                <GhostBtn onClick={() => setCsvOpen(true)} testId="clients-import"><span className="inline-flex items-center gap-1.5"><Upload size={14} /> Import CSV</span></GhostBtn>
                <PrimaryBtn onClick={() => setEditClient(null)} testId="clients-add"><Plus size={14} /> Add client</PrimaryBtn>
            </div>

            {rows.length === 0 ? (
                <EmptyState title="No clients yet" body="Convert a lead, add a client manually, or import your existing client list via CSV." testId="clients-empty" />
            ) : (
                <div className="rounded-2xl border border-oth-line/70 bg-white overflow-x-auto">
                    <div className="min-w-[1180px]">
                        <div className="grid gap-3 px-5 py-3 bg-[#EFEFEA] text-[11px] uppercase tracking-[0.16em] text-oth-ink3 font-body" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
                            <SortHead label="Name" k="name" span="col-span-2" sort={sort} onSort={onSort} testId="clients-sort-name" />
                            <div className="col-span-2">Contact</div>
                            <SortHead label="ISB Batch" k="isb_batch" span="col-span-1" sort={sort} onSort={onSort} testId="clients-sort-isb" />
                            <div className="col-span-2">Categories</div>
                            <div className="col-span-1">Will</div>
                            <SortHead label="Birthday" k="birthday" span="col-span-1" sort={sort} onSort={onSort} testId="clients-sort-birthday" />
                            <div className="col-span-1">Family</div>
                            <SortHead label="Work / Status" k="pending_work" span="col-span-2" sort={sort} onSort={onSort} testId="clients-sort-status" />
                            <div className="col-span-2 text-right">Actions</div>
                        </div>
                        {rows.map((c, i) => (
                            <motion.div key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.015, 0.3) }}
                                style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}
                                className="grid gap-3 px-5 py-3.5 border-t border-oth-line/60 text-sm font-body items-center hover:bg-[#F5F5F2]/60 transition-colors" data-testid={`client-row-${c.id}`}>
                                <button onClick={() => setNotesClient(c)} className="col-span-2 text-left font-medium text-oth-ink hover:text-oth-blue truncate" data-testid={`client-name-${c.id}`}>
                                    {c.name}{c.company && <span className="block text-[11px] text-oth-ink3 font-normal truncate">{c.company}</span>}
                                </button>
                                <div className="col-span-2 min-w-0">
                                    {c.email ? <a href={`mailto:${c.email}`} className="block truncate text-oth-ink2 hover:text-oth-blue">{c.email}</a> : <span className="text-oth-ink3 italic text-xs">no email</span>}
                                    {c.phone ? <span className="block text-[12px] text-oth-ink3">{c.phone}</span> : null}
                                </div>
                                <div className="col-span-1 text-[12px] text-oth-ink2 truncate" data-testid={`client-isb-${c.id}`}>{c.isb_batch || "—"}</div>
                                <div className="col-span-2 flex flex-wrap gap-1">{(c.categories || []).map((t) => <Tag key={t}>{t}</Tag>)}</div>
                                <div className="col-span-1">{c.will_created ? <span className="inline-flex items-center gap-1 text-[11px] text-[#0B6B3A] font-medium"><Check size={13} /> Yes</span> : <span className="text-[11px] text-oth-ink3">No</span>}</div>
                                <div className="col-span-1 text-[12px] text-oth-ink3">{c.birthday ? fmtBday(c.birthday) : "—"}</div>
                                <div className="col-span-1 text-[12px] text-oth-ink3 truncate">{c.family_id ? (familyName(c.family_id) || "—") : "—"}</div>
                                <div className="col-span-2">
                                    <div className="flex flex-col items-start gap-1">
                                    {c.pending_work > 0
                                        ? <HoverCard openDelay={120} closeDelay={80}>
                                            <HoverCardTrigger asChild>
                                                <button onClick={() => setNotesClient(c)} title={`${c.pending_work} pending work item${c.pending_work > 1 ? "s" : ""}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#B5651D]/12 text-[#B5651D] hover:bg-[#B5651D]/20 transition-colors" data-testid={`client-status-${c.id}`}><Clock size={11} /> Pending{c.pending_work > 1 ? ` (${c.pending_work})` : ""}</button>
                                            </HoverCardTrigger>
                                            <HoverCardContent align="start" className="w-80 p-3" data-testid={`client-pending-popover-${c.id}`}>
                                                <div className="text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body mb-2">Latest pending work</div>
                                                <div className="space-y-2.5">
                                                    {(c.pending_work_notes || []).map((n, idx) => (
                                                        <div key={idx} className="flex items-start gap-2" data-testid={`client-pending-note-${c.id}-${idx}`}>
                                                            <Clock size={12} className="text-[#B5651D] mt-0.5 shrink-0" />
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="text-[13px] font-body text-oth-ink font-medium truncate">{n.subject || "Untitled task"}</span>
                                                                    {n.tag && <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-body border ${workTagClasses(tagColor(n.tag))}`}>{n.tag}</span>}
                                                                </div>
                                                                {stripHtml(n.body_html) && <div className="text-[12px] text-oth-ink2 font-body leading-snug line-clamp-3 whitespace-pre-wrap">{stripHtml(n.body_html)}</div>}
                                                                {n.note_date && <div className="text-[11px] text-oth-ink3 font-body mt-0.5">{String(n.note_date).slice(0, 10)}</div>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {c.pending_work > (c.pending_work_notes || []).length && (
                                                    <div className="mt-2 pt-2 border-t border-oth-line/60 text-[11px] text-oth-ink3 font-body">+{c.pending_work - (c.pending_work_notes || []).length} more — click to view all</div>
                                                )}
                                            </HoverCardContent>
                                          </HoverCard>
                                        : <span className="text-[11px] text-oth-ink3" data-testid={`client-status-${c.id}`}>—</span>}
                                    {(c.work_tags || []).length > 0 && (
                                        <div className="flex flex-wrap gap-1" data-testid={`client-worktags-${c.id}`}>
                                            {c.work_tags.map((t) => <span key={t} className={`px-1.5 py-0.5 rounded-full text-[10px] font-body border ${workTagClasses(tagColor(t))}`} data-testid={`client-worktag-${c.id}-${t}`}>{t}</span>)}
                                        </div>
                                    )}
                                    </div>
                                </div>
                                <div className="col-span-2 flex items-center justify-end gap-0.5">
                                    <IconBtn onClick={() => setEditClient(c)} title="Edit" testId={`client-edit-${c.id}`}><Pencil size={15} /></IconBtn>
                                    <IconBtn onClick={() => setNotesClient(c)} title="Notes / work" testId={`client-notes-${c.id}`}><NotebookPen size={15} /></IconBtn>
                                    <IconBtn onClick={() => setFollowClient(c)} title="Schedule follow-up" testId={`client-followup-${c.id}`}><CalendarClock size={15} /></IconBtn>
                                    <a href={waLink(c.phone, `Hi ${c.name},`)} target="_blank" rel="noreferrer" title="WhatsApp" className="w-8 h-8 rounded-lg flex items-center justify-center text-oth-ink2 hover:text-[#0B6B3A] hover:bg-[#EFEFEA]" data-testid={`client-wa-${c.id}`}><MessageCircle size={15} /></a>
                                    <IconBtn onClick={() => doDelete(c)} title="Delete" danger testId={`client-delete-${c.id}`}><Trash2 size={15} /></IconBtn>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}

            {editClient !== undefined && <ClientModal key={editClient?.id ?? "new"} onClose={() => setEditClient(undefined)} client={editClient} config={config} clients={clients} onSaved={reload} />}
            {notesClient && <NotesModal key={notesClient.id} onClose={() => { setNotesClient(null); reload(); }} entity={notesClient} entityType="client" allowWork config={config} onConfigChange={reloadConfig} />}
            {followClient && <FollowupModal key={followClient.id} onClose={() => setFollowClient(null)} entity={followClient} entityType="client" />}
            <CsvImportModal open={csvOpen} onClose={() => setCsvOpen(false)} onDone={reload} />
        </div>
    );
};
